import { Controller } from "@hotwired/stimulus";
// TinyMCE loads on demand: bootEditor() imports ../tinymce-loader, which assigns `window.tinymce`
// for side-effects, so a page without an editor never downloads it. We reference the global here
// rather than re-importing the module, and the type import is elided at runtime.
import type { Editor, RawEditorOptions } from "tinymce";
import { tryRestoreDeferredRichTextFocus } from "../focus-restore-policy";
import { loadRichTextEditorSkin } from "../rich-text-editor-skin";
import { richTextEditorOptions } from "./rich-text-editor-options";
import { announceEditor } from "./rich-text-editor-announce";
import { stripEditorResidue } from "./rich-text-editor-residue";
import { addPathBase, stripPathBase } from "./rich-text-editor-path-base";
import { captureInvalidMarking, restoreInvalidMarking } from "./rich-text-editor-invalid-marking";
import { RichTextEditorLoadingState } from "./rich-text-editor-loading-state";
import { uploadFailureMessage } from "./rich-text-editor-upload-failure";
import { MentionPicker, type MentionPerson } from "./rich-text-editor-mention-picker";

declare global {
    interface Window {
        tinymce: {
            init(options: RawEditorOptions): Promise<Editor[]>;
            remove(selector: string | Editor): void;
            get(id: string): Editor | null;
        };
    }
}

interface UploadResponse {
    location: string;
}

// Fired by Turbo/idiomorph on every element it reconciles during a morph refresh, target = the
// element that was morphed. Bubbles, so listening on the host element and filtering by `target`
// scopes this to the controller's own instance even when several editors share a page.
const MORPH_ELEMENT_EVENT = "turbo:morph-element";

// TinyMCE events that can change the authored content: typing, toolbar commands, undo/redo, and
// programmatic sets. Every one of them re-syncs the hidden input.
const CONTENT_CHANGED_EVENTS = "input change ExecCommand Undo Redo SetContent";

// Dispatched from the host on every editor `init`; `detail.editor` is the TinyMCE editor and
// `detail.contentChangedEvents` is CONTENT_CHANGED_EVENTS.
const READY_EVENT = "rich-text-editor:ready";

/**
 * Stimulus controller that boots a TinyMCE instance against the host's editable target.
 *
 * TinyMCE runs in **inline mode**: the editable region is a contenteditable element in the host
 * document rather than an iframe, so the app's own `.prose` stylesheet styles the authored content
 * and editing matches reading. This is deliberate and load-bearing: an iframe does not inherit the
 * host page's stylesheet, so authored content would render in the browser default font. Two
 * consequences shape the code below:
 *
 * - **We own the posted value.** Inline mode cannot attach to a `<textarea>` (it silently no-ops),
 *   so the value travels in a sibling hidden input. TinyMCE's own `hidden_input` auto-field is
 *   disabled because it names the field after the editor id, and call sites legitimately post one
 *   field name from several editors with distinct ids. `add_form_submit_trigger` is off for the same
 *   reason: `editor.save()` writes into any field whose name matches the editor id, which would make
 *   TinyMCE a second writer of a field this controller already owns.
 * - **We must not configure content CSS.** In inline mode `content_css`/`content_style` inject into
 *   the host document's `<head>` unscoped — that is how a previous `body { margin: 1rem }` leak
 *   reached every page. `skin: false` additionally suppresses TinyMCE's own mandatory content CSS,
 *   leaving the app stylesheet as the sole authority over the editable region.
 *
 * Mentions: when `people` is a non-empty Stimulus value (from optional `IRichTextEditorPeopleSource`),
 * typing `@` as a new token opens a searchable People list. Choosing a Person inserts
 * `<span class="mention" data-person-id="…">@name</span>`. Empty or omitted people leave `@` alone.
 *
 * Loading state: the component renders the host `aria-busy` with a `loading` status line. Both are
 * removed once the editor has started, or when its download fails, so a box that cannot become an
 * editor looks as the server rendered it. A press on the editable area while it loads puts the
 * caret in it once the editor is ready.
 *
 * Morph-aware. A Turbo morph treats an editor in one of three ways:
 *
 * - **The host stays.** The morph reconciles its children back to the server markup, stripping
 *   TinyMCE's chrome. Stimulus never re-runs `connect()`, so the controller re-boots on
 *   `turbo:morph-element`.
 * - **Host and editable area are both replaced.** Stimulus's ordinary disconnect()/connect()
 *   handles it.
 * - **The editable area moves into a new host**, because idiomorph keeps it by id while the
 *   markup around it differs (a 422 re-render of another page's form,
 *   `docs/adr/0007-validation-re-renders-return-422.md`). Stimulus can connect the new host
 *   before it disconnects the old one, and TinyMCE refuses to start a second editor on an id it
 *   still holds.
 */
export default class extends Controller {
    static override targets = ["editable", "value", "toolbar", "dialog", "search", "options", "empty", "loading"];
    static override values = {
        toolbar: { type: Boolean, default: true },
        autofocus: { type: Boolean, default: false },
        fill: { type: Boolean, default: false },
        uploadUrl: { type: String, default: "" },
        uploadFailed: { type: String, default: "{status}" },
        pathBase: { type: String, default: "" },
        language: { type: String, default: "en" },
        placeholder: { type: String, default: "" },
        labelledBy: { type: String, default: "" },
        people: { type: Array, default: [] },
    };

    declare editableTarget: HTMLElement;
    declare readonly hasEditableTarget: boolean;
    declare valueTarget: HTMLInputElement;
    declare readonly hasValueTarget: boolean;
    declare toolbarTarget: HTMLElement;
    declare readonly hasToolbarTarget: boolean;
    declare dialogTarget: HTMLDialogElement;
    declare readonly hasDialogTarget: boolean;
    declare searchTarget: HTMLInputElement;
    declare optionsTarget: HTMLUListElement;
    declare emptyTarget: HTMLElement;
    declare loadingTarget: HTMLElement;
    declare readonly hasLoadingTarget: boolean;
    declare toolbarValue: boolean;
    declare autofocusValue: boolean;
    declare fillValue: boolean;
    declare uploadUrlValue: string;
    declare uploadFailedValue: string;
    declare pathBaseValue: string;
    declare languageValue: string;
    declare placeholderValue: string;
    declare labelledByValue: string;
    declare peopleValue: MentionPerson[];

    private editor: Editor | null = null;
    private form: HTMLFormElement | null = null;

    // The content as of the last dispatched `change` (or the boot-time silent sync). Blur only
    // announces a `change` when the content moved past this baseline, so an auto-submit form is
    // never asked to save content it already has.
    private lastAnnouncedChangeValue = "";

    // The editable element's server-rendered class list, captured before TinyMCE adds its own.
    // Restored on teardown so the prose classes survive but `mce-content-body` does not.
    private originalEditableClassName = "";

    // Bumped on every disconnect and every re-boot. A bootEditor() call whose generation has gone
    // stale by the time TinyMCE's async init() resolves tears down its own result instead of
    // adopting it, so a disconnect (or a morph re-init) racing a pending startup can never orphan
    // an editor registered under the editable element's id.
    private generation = 0;

    private readonly loadingState = new RichTextEditorLoadingState({
        host: () => this.element as HTMLElement,
        editable: () => this.currentEditable(),
        loading: () => (this.hasLoadingTarget ? this.loadingTarget : null),
    });

    private readonly mentionPicker = new MentionPicker({
        editor: () => this.editor,
        editable: () => this.currentEditable(),
        dialog: () => (this.hasDialogTarget ? this.dialogTarget : null),
        search: () => this.searchTarget,
        options: () => this.optionsTarget,
        empty: () => this.emptyTarget,
        people: () => this.peopleValue,
    });

    private readonly handleMorphElement = (event: Event): void => {
        if (event.target !== this.element) return;
        this.mentionPicker.close();
        const invalidMarking = captureInvalidMarking(this.currentEditable());
        this.teardownEditor();
        restoreInvalidMarking(this.currentEditable(), invalidMarking);
        void this.bootEditor();
    };

    // Belt-and-braces: continuous sync should already have written the current content, but a
    // re-serialize on submit means a missed change event can never post stale HTML.
    private readonly handleFormSubmit = (): void => {
        if (this.editor) {
            this.syncValue(this.editor);
        }
    };

    private readonly onWindowClick = (event: MouseEvent): void => {
        if (!this.mentionPicker.isOpen) return;
        const target = event.target;
        if (target instanceof Node && this.element.contains(target)) return;
        this.mentionPicker.close();
    };

    private currentEditable(): HTMLElement | null {
        return this.hasEditableTarget ? this.editableTarget : null;
    }

    public connect(): void {
        this.originalEditableClassName = this.editableTarget.className;
        this.form = this.element.closest("form");
        this.form?.addEventListener("submit", this.handleFormSubmit);
        this.element.addEventListener(MORPH_ELEMENT_EVENT, this.handleMorphElement);
        window.addEventListener("click", this.onWindowClick);
        void this.bootEditor();
    }

    public disconnect(): void {
        this.element.removeEventListener(MORPH_ELEMENT_EVENT, this.handleMorphElement);
        window.removeEventListener("click", this.onWindowClick);
        this.form?.removeEventListener("submit", this.handleFormSubmit);
        this.form = null;
        this.mentionPicker.close();
        this.generation++;
        this.loadingState.reset();
        this.teardownEditor();
    }

    public onMentionSearch(): void {
        this.mentionPicker.onSearch();
    }

    public onMentionSearchKeydown(event: KeyboardEvent): void {
        this.mentionPicker.onSearchKeydown(event);
    }

    private async bootEditor(): Promise<void> {
        const generation = ++this.generation;
        this.loadingState.begin();
        const invalidMarking = captureInvalidMarking(this.currentEditable());
        const hasUpload = this.uploadUrlValue !== "";
        const showToolbar = this.toolbarValue && this.hasToolbarTarget;
        try {
            await Promise.all([import("../tinymce-loader"), loadRichTextEditorSkin()]);
        } catch (error) {
            // The element stays as the server rendered it, with its hidden input untouched.
            console.error("The rich-text editor could not be loaded.", error);
            if (generation === this.generation) this.loadingState.finish();
            return;
        }
        // A disconnect or a newer boot during the downloads leaves nothing to start.
        if (generation !== this.generation) return;
        this.removeStaleEditor();
        restoreInvalidMarking(this.currentEditable(), invalidMarking);
        const editors = await window.tinymce.init({
            ...richTextEditorOptions({
                target: this.editableTarget,
                language: this.languageValue,
                placeholder: this.placeholderValue,
                toolbarContainer: showToolbar ? this.toolbarTarget : null,
                uploadHandler: hasUpload ? this.handleUpload.bind(this) : null,
            }),
            // `setup` runs — and the `init` event fires — before init()'s promise resolves, so
            // `this.editor` is not assigned yet. Both handlers close over the editor they were
            // given rather than reading the field.
            setup: (editor: Editor) => {
                // TinyMCE fires SetContent with the server-rendered HTML before `init`; that load is
                // the silent boot sync's to announce, not an edit.
                let booted = false;
                editor.on(CONTENT_CHANGED_EVENTS, () => {
                    if (!booted) return;
                    // Turbo unregisters targets before disconnect(); TinyMCE still fires a leftover
                    // input from addNonTypingUndoLevel during remove().
                    if (!this.hasEditableTarget) return;
                    this.syncValue(editor);
                    this.mentionPicker.onEditorInput();
                });
                editor.on("keydown", (event) => {
                    this.mentionPicker.onEditorKeydown(event as unknown as KeyboardEvent);
                });
                // Blur announces the content as a `change`, distinct from the `input` events fired
                // during typing, so a surrounding form that opts into auto-submit-on-change saves
                // once editing stops rather than on every keystroke. Nothing is announced when the
                // content has not moved since the last announced change — focusing and clicking
                // away without editing must not trigger a save.
                editor.on("blur", () => {
                    this.syncValue(editor, "change");
                });
                announceEditor(editor, this.placeholderValue, this.labelledByValue);
                editor.on("init", () => {
                    booted = true;
                    // A boot that a newer boot or a disconnect has overtaken leaves the state alone.
                    let pressed = false;
                    if (generation === this.generation) {
                        pressed = this.loadingState.wasPressed();
                        this.loadingState.finish();
                    }
                    // TinyMCE normalises the server-rendered HTML on load; sync once so the posted
                    // value matches what the author sees even if they never touch the field.
                    // Silently — normalisation is not a user edit, and announcing it would mark a
                    // pristine form dirty and trigger an unsaved-changes prompt on first navigation.
                    this.syncValue(editor, "silent");
                    if (this.autofocusValue || pressed) {
                        editor.focus();
                    }
                    // Fires on every boot, the morph re-boot included, so a page controller that
                    // decorates the live editor never has to poll for it.
                    this.element.dispatchEvent(
                        new CustomEvent(READY_EVENT, {
                            bubbles: true,
                            detail: { editor, contentChangedEvents: CONTENT_CHANGED_EVENTS },
                        }),
                    );
                });
            },
        });
        const editor = editors[0] ?? null;

        if (generation !== this.generation) {
            // A disconnect or a newer boot invalidated this attempt while TinyMCE was starting
            // up; tear down the now-orphaned instance rather than adopt it.
            if (editor) {
                window.tinymce.remove(editor);
                if (this.hasEditableTarget) stripEditorResidue(this.editableTarget, this.originalEditableClassName);
            }
            return;
        }

        this.editor = editor;

        // Same-page morph/frame remount: suite focus policy defers caret restore until the editor
        // is live again. Autofocus (handled above on init) still wins via the policy guardrails.
        tryRestoreDeferredRichTextFocus(this.editableTarget);
    }

    /**
     * An editor that holds this id on another element is a page fault, not ours to repair.
     */
    private removeStaleEditor(): void {
        const editable = this.editableTarget;
        const held = editable.id === "" ? null : window.tinymce.get(editable.id);
        if (held && held.getElement() === editable) {
            window.tinymce.remove(held);
        }
    }

    private teardownEditor(): void {
        if (this.editor) {
            window.tinymce.remove(this.editor);
            this.editor = null;
            if (this.hasEditableTarget) stripEditorResidue(this.editableTarget, this.originalEditableClassName);
        }
    }

    /**
     * Writes the editor's current content into the hidden input and announces it. The editor shows
     * addresses under the page's path base; the input gets them without it, as they are stored.
     *
     * A programmatic `.value` assignment fires nothing, so anything listening for edits — dirty
     * tracking and its unsaved-changes prompt, character counters, any future autosave — would
     * never hear a rich-text change. The mode says how the sync is announced:
     *
     * - `"input"` (typing): dispatches a bubbling `input` event when the value actually changed
     *   since the previous sync, so the hidden field behaves like an ordinary form control.
     * - `"change"` (blur): dispatches a bubbling `change` event only when the content differs from
     *   the last announced `change` (or the boot-time baseline), so focusing the editor and
     *   clicking away without editing never fires a save — and typing never fires `change`.
     * - `"silent"` (boot-time normalisation): announces nothing and resets the `change` baseline —
     *   normalisation is not a user edit, so a blur before any edit must not announce a change.
     */
    private syncValue(editor: Editor, mode: "input" | "change" | "silent" = "input"): void {
        if (!this.hasValueTarget) return;

        const html = stripPathBase(this.pathBaseValue, editor.getContent());
        const valueChanged = this.valueTarget.value !== html;

        if (valueChanged) {
            this.valueTarget.value = html;
        }

        switch (mode) {
            case "silent":
                this.lastAnnouncedChangeValue = html;
                break;
            case "change":
                if (html !== this.lastAnnouncedChangeValue) {
                    this.lastAnnouncedChangeValue = html;
                    this.valueTarget.dispatchEvent(new Event("change", { bubbles: true }));
                }
                break;
            case "input":
                if (valueChanged) {
                    this.valueTarget.dispatchEvent(new Event("input", { bubbles: true }));
                }
                break;
        }
    }

    private async handleUpload(blobInfo: { blob: () => Blob; filename: () => string }): Promise<string> {
        const form = new FormData();
        form.append("file", blobInfo.blob(), blobInfo.filename());

        const response = await fetch(this.uploadUrlValue, {
            method: "POST",
            body: form,
            credentials: "same-origin",
        });
        if (!response.ok) {
            throw new Error(await uploadFailureMessage(response, this.uploadFailedValue));
        }

        const body = (await response.json()) as UploadResponse;
        return addPathBase(this.pathBaseValue, body.location);
    }
}
